import { useState, useEffect, useCallback, useSyncExternalStore } from "react";
import { supabase } from "@/lib/supabase";
import {
  type Product,
  products as seedProducts,
  getLiveProductById,
  getCatalogProductById,
  parseProductDimensions,
  getProductBgImagePath,
  cleanSeoKeywords,
} from "@/lib/products";
import { type ExtendedProduct } from "@/features/admin/products/store/admin-products-store";

function convertExtendedToProduct(p: ExtendedProduct): Product {
  return {
    id: p.id,
    sku: p.sku || `VRD-${p.id}`,
    name: p.name,
    category: p.category,
    price: Number(p.price) || 99,
    rating: p.rating,
    reviewsCount: p.reviewsCount,
    image: p.image,
    hoverImage: p.hoverImage,
    gallery: p.gallery,
    note: p.note,
    description: p.description,
    dimensions: p.dimensions,
    potDetails: p.potDetails,
    careInstructions: p.careInstructions,
    inStock: p.inStock !== false && (p.stockQuantity === undefined || p.stockQuantity > 0),
    stockQuantity: p.stockQuantity ?? (p.inStock ? 50 : 0),
    badge: p.badge,
    foliageMaterial: p.foliageMaterial,
    foliageSpread: p.foliageSpread,
    usageType: p.usageType,
    mossTopping: p.mossTopping,
    trunkType: p.trunkType,
    weight: p.weight,
    lengthCm: p.lengthCm,
    widthCm: p.widthCm,
    heightCm: p.heightCm,
  };
}

function getInitialProducts(): Product[] {
  // Returns the seed catalogue as a deterministic pre-DB snapshot. The localStorage admin
  // cache is intentionally NOT used here: a stale admin snapshot used to shadow the database
  // and hide rows (e.g. new categories) that the admin had never opened/saved. Per-product
  // admin overrides are still applied by mapDbToProduct() once the DB rows arrive, so the
  // user-facing behaviour is unchanged for products an admin has actually edited.
  return seedProducts;
}

// Centralized mapper from database row to Product entity
export interface ProductDbRow {
  id: string;
  slug?: string | null;
  name?: string | null;
  category?: string | null;
  price?: number | null;
  rating?: number | null;
  reviews_count?: number | null;
  image?: string | null;
  hover_image?: string | null;
  gallery?: string[] | string | null;
  note?: string | null;
  description?: string | null;
  dimensions?: string | null;
  pot_details?: string | null;
  care_instructions?: string | null;
  foliage_spread?: string | null;
  weight?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  foliage_material?: string | null;
  trunk_type?: string | null;
  moss_topping?: string | null;
  usage_type?: string | null;
  stock?: number | null;
  in_stock?: boolean | null;
  badge?: string | null;
}

/** Shape of a `postgres_changes` realtime payload for the products table. */
interface RealtimeProductPayload {
  new?: { id?: string; slug?: string } | null;
  old?: { id?: string; slug?: string } | null;
}

export function mapDbToProduct(row: ProductDbRow): Product {
  let gallery: string[] = [];
  if (Array.isArray(row.gallery)) {
    gallery = row.gallery;
  } else if (typeof row.gallery === "string") {
    try {
      gallery = JSON.parse(row.gallery);
    } catch {
      gallery = [row.image, row.hover_image].filter((img): img is string => Boolean(img));
    }
  } else {
    gallery = [row.image, row.hover_image].filter((img): img is string => Boolean(img));
  }

  const price = Number(row.price);
  const validPrice = !isNaN(price) && price > 0 ? price : 99;

  // Prioritize active admin modifications
  const storedList: ExtendedProduct[] = [];
  const stored = storedList.find(
    (p) => p.id === row.id || p.sku === row.id || (p.slug && p.slug === row.slug),
  );
  const seedFallback = seedProducts.find((p) => p.id === row.id || p.sku === row.id);

  const isLegacyCategory = (cat?: string | null) =>
    !cat ||
    [
      "bonsai trees",
      "flowering plants",
      "indoor plants",
      "decorative topiary",
      // DB rows stamped "Uncategorized" carry no real classification — resolve from the
      // seed catalogue instead of surfacing a category no slider or filter matches.
      "uncategorized",
    ].includes(cat.toLowerCase().trim());

  const resolvedCategory =
    (!isLegacyCategory(stored?.category) && stored?.category) ||
    (!isLegacyCategory(row.category) && row.category) ||
    seedFallback?.category ||
    // A row with no classification anywhere (e.g. a test row created in Admin) keeps its
    // literal value so it stays visible and fixable in Admin rather than being silently
    // filed under a real category it does not belong to.
    row.category ||
    "Flower Plants and Pots";

  const fallbackSpecs = parseProductDimensions(
    seedFallback || {
      id: row.id,
      name: stored?.name || row.name || undefined,
      category: resolvedCategory,
      dimensions: stored?.dimensions || row.dimensions || undefined,
      heightCm:
        stored?.heightCm ??
        (row.height_cm !== null && row.height_cm !== undefined ? Number(row.height_cm) : undefined),
      widthCm:
        stored?.widthCm ??
        (row.width_cm !== null && row.width_cm !== undefined ? Number(row.width_cm) : undefined),
      lengthCm:
        stored?.lengthCm ??
        (row.length_cm !== null && row.length_cm !== undefined ? Number(row.length_cm) : undefined),
      weight:
        stored?.weight ??
        (row.weight !== null && row.weight !== undefined ? Number(row.weight) : undefined),
    },
  );

  return {
    id: row.id,
    sku: row.slug ? `VRD-${row.slug.toUpperCase()}` : `VRD-${row.id}`,
    name: stored?.name || row.name || seedFallback?.name || "Botanical Item",
    category: resolvedCategory,
    price: stored?.price || validPrice,
    rating: Number(stored?.rating || row.rating) || 5.0,
    reviewsCount: Number(stored?.reviewsCount || row.reviews_count) || 0,
    image: stored?.image || row.image || seedFallback?.image || "/plantpicture/p1/5.webp",
    hoverImage: (() => {
      const bg = getProductBgImagePath(row.id);
      return (
        (stored?.hoverImage &&
        !stored.hoverImage.includes("posters") &&
        stored.hoverImage !== stored.image
          ? stored.hoverImage
          : null) ||
        (row.hover_image && !row.hover_image.includes("posters") && row.hover_image !== row.image
          ? row.hover_image
          : null) ||
        bg ||
        seedFallback?.hoverImage ||
        row.hover_image ||
        row.image ||
        seedFallback?.image ||
        "/plantpicture/p1/5.webp"
      );
    })(),
    gallery: (() => {
      const bg = getProductBgImagePath(row.id);
      const hover =
        (stored?.hoverImage &&
        !stored.hoverImage.includes("posters") &&
        stored.hoverImage !== stored.image
          ? stored.hoverImage
          : null) ||
        (row.hover_image && !row.hover_image.includes("posters") && row.hover_image !== row.image
          ? row.hover_image
          : null) ||
        bg ||
        seedFallback?.hoverImage;

      const rawGal =
        stored?.gallery && stored.gallery.length > 0
          ? stored.gallery
          : gallery.length > 0
            ? gallery
            : seedFallback?.gallery || [row.image || "/plantpicture/p1/5.webp"];
      const cleanGal = (Array.isArray(rawGal) ? rawGal : []).filter(
        (img: string) => typeof img === "string" && !img.includes("posters"),
      );

      const combined = Array.from(
        new Set([
          row.image || seedFallback?.image || "/plantpicture/p1/5.webp",
          hover,
          bg,
          ...cleanGal,
          ...(Array.isArray(seedFallback?.gallery) ? seedFallback.gallery : []),
        ]),
      ).filter(
        (img): img is string => typeof img === "string" && Boolean(img) && !img.includes("posters"),
      );

      return combined.length > 0
        ? combined
        : [row.image || seedFallback?.image || "/plantpicture/p1/5.webp"];
    })(),
    note: stored?.note || row.note || seedFallback?.note || "",
    description: cleanSeoKeywords(
      stored?.description || row.description || seedFallback?.description || "",
    ),
    dimensions:
      stored?.dimensions || row.dimensions || seedFallback?.dimensions || fallbackSpecs.dimensions,
    potDetails:
      stored?.potDetails ||
      row.pot_details ||
      seedFallback?.potDetails ||
      fallbackSpecs.potDetails ||
      "Planter Included",
    careInstructions:
      stored?.careInstructions ||
      row.care_instructions ||
      seedFallback?.careInstructions ||
      fallbackSpecs.careInstructions ||
      "Wipe foliage gently with a soft dry cloth.",
    inStock:
      stored?.stockQuantity !== undefined
        ? stored.stockQuantity > 0 && stored.inStock !== false
        : stored?.inStock !== undefined
          ? stored.inStock
          : row.in_stock !== false &&
            (row.stock === null || row.stock === undefined || Number(row.stock) > 0),
    stockQuantity:
      stored?.stockQuantity !== undefined
        ? stored.stockQuantity
        : row.stock !== null && row.stock !== undefined
          ? Number(row.stock)
          : 50,
    badge: stored?.badge || row.badge || seedFallback?.badge || undefined,
    slug: row.slug
      ? row.slug.toLowerCase().trim().replace(/[\s_]+/g, "-").replace(/^-|-$/g, "")
      : stored?.slug || seedFallback?.slug || undefined,
    variants: seedFallback?.variants,
    foliageMaterial:
      stored?.foliageMaterial ||
      row.foliage_material ||
      seedFallback?.foliageMaterial ||
      fallbackSpecs.foliageMaterial,
    foliageSpread:
      stored?.foliageSpread ||
      row.foliage_spread ||
      seedFallback?.foliageSpread ||
      fallbackSpecs.foliageSpread,
    usageType:
      stored?.usageType || row.usage_type || seedFallback?.usageType || fallbackSpecs.usageType,
    mossTopping:
      stored?.mossTopping ||
      row.moss_topping ||
      seedFallback?.mossTopping ||
      fallbackSpecs.mossTopping,
    trunkType:
      stored?.trunkType || row.trunk_type || seedFallback?.trunkType || fallbackSpecs.trunkType,
    weight:
      stored?.weight !== undefined && stored.weight !== null
        ? Number(stored.weight)
        : row.weight !== undefined && row.weight !== null
          ? Number(row.weight)
          : (seedFallback?.weight ?? fallbackSpecs.weight),
    lengthCm:
      stored?.lengthCm !== undefined && stored.lengthCm !== null
        ? Number(stored.lengthCm)
        : row.length_cm !== undefined && row.length_cm !== null
          ? Number(row.length_cm)
          : (seedFallback?.lengthCm ?? fallbackSpecs.lengthCm),
    widthCm:
      stored?.widthCm !== undefined && stored.widthCm !== null
        ? Number(stored.widthCm)
        : row.width_cm !== undefined && row.width_cm !== null
          ? Number(row.width_cm)
          : (seedFallback?.widthCm ?? fallbackSpecs.widthCm),
    heightCm:
      stored?.heightCm !== undefined && stored.heightCm !== null
        ? Number(stored.heightCm)
        : row.height_cm !== undefined && row.height_cm !== null
          ? Number(row.height_cm)
          : (seedFallback?.heightCm ?? fallbackSpecs.heightCm),
  };
}

// ---------------------------------------------------------------------------------------
// Shared catalogue store.
//
// useLiveProducts used to be a plain useState/useEffect hook, so every component calling it
// fetched the whole catalogue for itself. Six call sites mount on the homepage alone
// (routes/index.tsx three times, WatchAndBuySection, LuxuryCategorySlider, search-modal),
// which meant six identical `products?select=*` round trips, six Supabase Realtime
// WebSocket channels -- each opened under its own random channel name -- and twelve
// BroadcastChannels, all carrying the same rows.
//
// The subscription now lives at module scope and is reference-counted: the first mount
// starts it, the last unmount tears it down. Every consumer reads one snapshot, so the work
// happens once no matter how many components ask for it.
//
// getSnapshot has to be referentially stable between real changes or useSyncExternalStore
// re-renders forever -- so `snapshot` is replaced only when the data actually changes, and
// never rebuilt per render.
// ---------------------------------------------------------------------------------------

type CatalogueSnapshot = {
  products: Product[];
  loading: boolean;
  error: Error | null;
};

let snapshot: CatalogueSnapshot | null = null;
const subscribers = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let stopSync: (() => void) | null = null;
let mountCount = 0;

function getSnapshot(): CatalogueSnapshot {
  if (!snapshot) {
    snapshot = { products: getInitialProducts(), loading: true, error: null };
  }
  return snapshot;
}

// React calls this during the prerender pass AND during hydration on the client, so it has
// to return exactly what the prerendered HTML was built from: the seed catalogue, never the
// localStorage admin cache. The third argument used to be `getSnapshot`, which defeated it --
// the cache is seeded at module load, so the first client render read admin data that injects
// detail fields no seed product has, and every product page hydrated with extra DOM nodes.
// React discarded the tree as error #418. Live data still arrives: once hydration finishes,
// useSyncExternalStore compares getSnapshot() and re-renders if it differs.
//
// Cached because the reference has to be stable, same reason as getSnapshot above.
let serverSnapshot: CatalogueSnapshot | null = null;

function getServerSnapshot(): CatalogueSnapshot {
  if (!serverSnapshot) {
    serverSnapshot = { products: seedProducts, loading: true, error: null };
  }
  return serverSnapshot;
}

function publish(next: Partial<CatalogueSnapshot>) {
  snapshot = { ...getSnapshot(), ...next };
  for (const notify of [...subscribers]) notify();
}

// Concurrent callers share one request instead of each issuing its own: a realtime event and
// a window event arriving together would otherwise fetch the same rows twice.
function fetchProducts(): Promise<void> {
  if (inFlight) return inFlight;

  publish({ loading: true });

  inFlight = (async () => {
    try {
      const { data, error: dbError } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });

      if (dbError) {
        publish({ products: getInitialProducts(), error: new Error(dbError.message) });
      } else if (data && data.length > 0) {
        const mapped = data.map(mapDbToProduct);
        const seedIndexMap = new Map<string, number>(
          seedProducts.map((p, idx) => [p.id, idx]),
        );
        mapped.sort((a, b) => {
          const idxA = seedIndexMap.get(a.id);
          const idxB = seedIndexMap.get(b.id);
          if (idxA !== undefined && idxB !== undefined) return idxA - idxB;
          if (idxA !== undefined) return -1;
          if (idxB !== undefined) return 1;
          return (a.id || "").localeCompare(b.id || "");
        });

        publish({ products: mapped, error: null });
      } else {
        publish({ products: getInitialProducts() });
      }
    } catch (err: unknown) {
      publish({
        products: getInitialProducts(),
        error: err instanceof Error ? err : new Error(String(err)),
      });
    } finally {
      inFlight = null;
      publish({ loading: false });
    }
  })();

  return inFlight;
}

function startSync(): () => void {
  // A single fixed channel name is safe now that only one channel is ever open. The random
  // suffix existed to stop the per-mount channels from colliding on one client.
  const channel = supabase
    .channel("products-live-sync")
    .on("postgres_changes", { event: "*", schema: "public", table: "products" }, () => {
      fetchProducts();
    })
    .subscribe();

  // Admin edits land in localStorage first, so surface them at once and then reconcile
  // against the database.
  const handleLocalUpdate = () => {
    publish({ products: getInitialProducts() });
    fetchProducts();
  };
  window.addEventListener("products_updated", handleLocalUpdate);
  window.addEventListener("inventory_updated", handleLocalUpdate);
  window.addEventListener("storage", handleLocalUpdate);

  let bc: BroadcastChannel | null = null;
  let invBc: BroadcastChannel | null = null;
  try {
    bc = new BroadcastChannel("products_sync");
    bc.onmessage = handleLocalUpdate;
    invBc = new BroadcastChannel("inventory_sync");
    invBc.onmessage = handleLocalUpdate;
  } catch {
    // Ignored
  }

  return () => {
    supabase.removeChannel(channel);
    window.removeEventListener("products_updated", handleLocalUpdate);
    window.removeEventListener("inventory_updated", handleLocalUpdate);
    window.removeEventListener("storage", handleLocalUpdate);
    bc?.close();
    invBc?.close();
  };
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);

  if (++mountCount === 1) {
    stopSync = startSync();
    fetchProducts();
  }

  return () => {
    subscribers.delete(notify);
    if (--mountCount === 0) {
      stopSync?.();
      stopSync = null;
    }
  };
}

export function useLiveProducts() {
  const { products, loading, error } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const getProduct = useCallback(
    (id: string): Product | undefined => {
      return products.find((p) => p.id === id || p.sku === id);
    },
    [products],
  );

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
    getProduct,
  };
}

/**
 * Fetch a single product directly from Supabase with realtime updates
 */
export function useLiveProduct(idOrSlug: string) {
  // Seed-only on purpose: this initializer runs during hydration, so reading the localStorage
  // cache here would diverge from the prerendered markup (see getServerSnapshot above). The
  // effect below replaces it with live data immediately after mount.
  const [product, setProduct] = useState<Product | null>(() => {
    return getCatalogProductById(idOrSlug) || null;
  });
  const [loading, setLoading] = useState(() => !getCatalogProductById(idOrSlug));

  const fetchSingleProduct = useCallback(async () => {
    if (!idOrSlug) return;
    const cleanParam = decodeURIComponent(idOrSlug).toLowerCase().trim();
    const slugParam = cleanParam.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    try {
      setLoading(true);
      const orFilter =
        slugParam && slugParam !== cleanParam
          ? `id.eq.${cleanParam},slug.eq.${cleanParam},slug.eq.${slugParam}`
          : `id.eq.${cleanParam},slug.eq.${cleanParam}`;

      const { data, error: dbError } = await supabase
        .from("products")
        .select("*")
        .or(orFilter)
        .maybeSingle();

      let resolvedData = data;
      if (!resolvedData && !dbError) {
        const { data: allRows } = await supabase.from("products").select("*");
        if (allRows) {
          resolvedData =
            allRows.find((r: ProductDbRow) => {
              const rSlug = (r.slug || "").toLowerCase().trim();
              const rNormSlug = rSlug.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              const rNameSlug = (r.name || "")
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
              return (
                r.id?.toLowerCase() === cleanParam ||
                r.id?.toLowerCase() === slugParam ||
                rSlug === cleanParam ||
                (slugParam && rNormSlug === slugParam) ||
                (slugParam && rNameSlug === slugParam) ||
                rNameSlug === cleanParam
              );
            }) || null;
        }
      }

      if (resolvedData) {
        setProduct(mapDbToProduct(resolvedData));
      } else {
        const fallback = getLiveProductById(cleanParam);
        setProduct(fallback || null);
      }
    } catch {
      const fallback = getLiveProductById(cleanParam);
      setProduct(fallback || null);
    } finally {
      setLoading(false);
    }
  }, [idOrSlug]);

  useEffect(() => {
    if (!product) {
      const localCached = getLiveProductById(idOrSlug);
      if (localCached) {
        setProduct(localCached);
        setLoading(false);
      }
    }
    fetchSingleProduct();

    const channelName = `product-live-sync-${idOrSlug}-${Math.random().toString(36).substring(2, 9)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "products" },
        (payload: RealtimeProductPayload) => {
          const clean = decodeURIComponent(idOrSlug).toLowerCase().trim();
          const norm = clean.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          const newSlug = payload.new?.slug?.toLowerCase()?.replace(/[^a-z0-9]+/g, "-");
          const oldSlug = payload.old?.slug?.toLowerCase()?.replace(/[^a-z0-9]+/g, "-");
          if (
            payload.new?.id === idOrSlug ||
            payload.old?.id === idOrSlug ||
            payload.new?.slug === idOrSlug ||
            payload.old?.slug === idOrSlug ||
            (norm && newSlug === norm) ||
            (norm && oldSlug === norm)
          ) {
            fetchSingleProduct();
          }
        },
      )
      .subscribe();

    const handleLocalUpdate = () => {
      const found = getLiveProductById(idOrSlug);
      if (found) setProduct(found);
      fetchSingleProduct();
    };

    window.addEventListener("products_updated", handleLocalUpdate);
    window.addEventListener("inventory_updated", handleLocalUpdate);
    window.addEventListener("storage", handleLocalUpdate);

    let invBc: BroadcastChannel | null = null;
    let prodBc: BroadcastChannel | null = null;
    try {
      invBc = new BroadcastChannel("inventory_sync");
      invBc.onmessage = () => {
        handleLocalUpdate();
      };
      prodBc = new BroadcastChannel("products_sync");
      prodBc.onmessage = () => {
        handleLocalUpdate();
      };
    } catch {
      // BroadcastChannel is unavailable in some browsers.
    }

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("products_updated", handleLocalUpdate);
      window.removeEventListener("inventory_updated", handleLocalUpdate);
      window.removeEventListener("storage", handleLocalUpdate);
      invBc?.close();
      prodBc?.close();
    };
  }, [idOrSlug, fetchSingleProduct]);

  return { product, loading, refetch: fetchSingleProduct };
}
